import {AdjustmentsHorizontalIcon, ChevronDownIcon, ExclamationTriangleIcon, MagnifyingGlassIcon, MapPinIcon} from "@heroicons/react/20/solid";
import {useState} from "react";
import type {TicketCostBucketKey, TicketCostInputDecision, TicketCostLine, TicketCostResolution} from "./ticket-cost-types";
import {compareTicketCostLines, formatTicketCostCurrency, formatTicketCostPercent, formatTicketCostRate, sortTicketCostLines} from "./ticket-cost-types";
import {TicketCostBucketIcon} from "./TicketCostComponentBreakdown";

export type TicketCostChargeListProps = {
  lines: TicketCostLine[];
  currency?: string;
  defaultGrouping?: TicketCostChargeGrouping;
  expectedTotal?: number;
  searchThreshold?: number;
  groupingPreferenceKey?: string;
};

export type TicketCostChargeGrouping = "component" | "well" | "charge" | "none";

const resolutionClasses: Record<TicketCostResolution["state"], string> = {
  system: "text-[var(--ots-text-muted)]",
  adjusted: "font-semibold text-[var(--ots-warning)]",
  override: "font-semibold text-[var(--ots-error)]",
};

const decisionDisplay: Record<TicketCostInputDecision["decision"], {label: string; title: string; classes: string}> = {
  APPLIED: {label: "Used", title: "Cost input used", classes: "bg-[var(--ots-success-container)] text-[var(--ots-success)]"},
  REJECTED: {label: "Not used", title: "Cost input not used", classes: "bg-[var(--ots-danger-container)] text-[var(--ots-error)]"},
  ERROR: {label: "Error", title: "Cost input error", classes: "bg-[var(--ots-danger-container)] text-[var(--ots-error)]"},
  IGNORED: {label: "Ignored", title: "Cost input ignored", classes: "bg-[var(--ots-neutral-container)] text-[var(--ots-on-neutral-container)]"},
  REVIEWED: {label: "Reviewed", title: "Cost input exists", classes: "bg-[var(--ots-neutral-container)] text-[var(--ots-on-neutral-container)]"},
};

const decisionPriority: TicketCostInputDecision["decision"][] = ["ERROR", "REJECTED", "APPLIED", "IGNORED", "REVIEWED"];

const getLineDecision = (inputs: TicketCostInputDecision[]) => {
  const decision = decisionPriority.find((candidate) => inputs.some((input) => input.decision === candidate)) ?? "REVIEWED";
  return decisionDisplay[decision];
};

const DecisionBadge = ({decision}: {decision: TicketCostInputDecision["decision"]}) => {
  const display = decisionDisplay[decision];
  return (
    <span title={display.title} className={["inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold", display.classes].join(" ")}>
      <AdjustmentsHorizontalIcon className="size-3" aria-hidden="true"/>
      {display.label}
    </span>
  );
};

const CostInputValue = ({label, value, cost, currency}: {label: string; value?: string; cost?: number; currency: string}) => (
  <div>
    <dt className="text-[10px] font-medium uppercase tracking-wide text-[var(--ots-text-muted)]">{label}</dt>
    <dd className="mt-0.5 text-[11px] font-medium text-[var(--ots-text)]">
      {value ?? "—"}
      {cost !== undefined && <span className="ml-1.5 tabular-nums text-[var(--ots-text-muted)]">({formatTicketCostCurrency(cost, currency)})</span>}
    </dd>
  </div>
);

const formatBasis = (line: TicketCostLine) => {
  if (line.quantity === undefined) return line.quantityLabel ?? "No quantity basis";
  const quantity = new Intl.NumberFormat("en-US", {maximumFractionDigits: 3}).format(line.quantity);
  return `${quantity}${line.quantityLabel ? ` ${line.quantityLabel}` : ""}`;
};

const formatBillableQuantity = (line: TicketCostLine) => line.billableQuantity === undefined
  ? "—"
  : new Intl.NumberFormat("en-US", {maximumFractionDigits: 3}).format(line.billableQuantity);

const resolvedChargeLabel = (line: TicketCostLine) => line.label.trim() || line.component?.label?.trim() || "Unlabeled charge";

type ChargeGroup = {
  key: string;
  label: string;
  detail?: string;
  bucket?: TicketCostBucketKey;
  lines: TicketCostLine[];
};

const groupingOptions: TicketCostChargeGrouping[] = ["component", "well", "charge", "none"];

const recommendedGrouping = (lines: TicketCostLine[]): TicketCostChargeGrouping => {
  const wellsByCharge = new Map<string, Set<string>>();
  lines.forEach((line) => {
    if (!line.wellName) return;
    const chargeKey = line.label.trim().toLowerCase();
    const wells = wellsByCharge.get(chargeKey) ?? new Set<string>();
    wells.add(line.wellId ?? line.wellName);
    wellsByCharge.set(chargeKey, wells);
  });
  return Array.from(wellsByCharge.values()).some((wells) => wells.size > 1) ? "well" : "component";
};

const readGroupingPreference = (key: string): TicketCostChargeGrouping | undefined => {
  if (typeof window === "undefined") return undefined;
  try {
    const value = window.localStorage.getItem(key) as TicketCostChargeGrouping | null;
    return value && groupingOptions.includes(value) ? value : undefined;
  } catch {
    return undefined;
  }
};

const saveGroupingPreference = (key: string, value: TicketCostChargeGrouping) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage may be unavailable in embedded/privacy-restricted contexts.
  }
};

const groupCharges = (lines: TicketCostLine[], grouping: TicketCostChargeGrouping): ChargeGroup[] => {
  const byAmountDescending = (left: TicketCostLine, right: TicketCostLine) =>
    right.amount - left.amount || compareTicketCostLines(left, right);
  if (grouping === "none") return [{key: "all", label: "All charges", lines: [...lines].sort(byAmountDescending)}];

  const groups = new Map<string, ChargeGroup>();
  lines.forEach((line) => {
    if (grouping === "component") {
      const key = line.component?.code ?? "unclassified";
      const existing = groups.get(key);
      if (existing) existing.lines.push(line);
      else groups.set(key, {key, label: line.component?.label ?? "Unclassified", bucket: line.bucket, lines: [line]});
      return;
    }

    const isWellGrouping = grouping === "well";
    const isChargeGrouping = grouping === "charge";
    const chargeLabel = resolvedChargeLabel(line);
    const key = isWellGrouping
      ? line.wellId ?? line.wellName ?? "ticket-level"
      : `${line.component?.code ?? "unclassified"}:${chargeLabel}`;
    const label = isWellGrouping ? line.wellName ?? "Ticket-level" : chargeLabel;
    const detail = isWellGrouping
      ? line.wellAfe ? `AFE ${line.wellAfe}` : undefined
      : isChargeGrouping && line.component?.label?.trim().toLocaleLowerCase("en-US") !== chargeLabel.toLocaleLowerCase("en-US")
        ? line.component?.label
        : undefined;
    const existing = groups.get(key);
    if (existing) existing.lines.push(line);
    else groups.set(key, {key, label, detail, lines: [line]});
  });
  return Array.from(groups.values())
    .map((group) => ({...group, lines: [...group.lines].sort(byAmountDescending)}))
    .sort((left, right) => left.label.localeCompare(right.label, undefined, {sensitivity: "base"})
      || (left.detail ?? "").localeCompare(right.detail ?? "", undefined, {sensitivity: "base"})
      || left.key.localeCompare(right.key));
};

/** Grouped charge ledger that keeps each amount adjacent to its calculation basis. */
export const TicketCostChargeList = ({
  lines,
  currency = "USD",
  defaultGrouping,
  expectedTotal,
  searchThreshold = 10,
  groupingPreferenceKey = "ots.ticket-cost.charge-grouping",
}: TicketCostChargeListProps) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [grouping, setGrouping] = useState<TicketCostChargeGrouping>(() => defaultGrouping ?? readGroupingPreference(groupingPreferenceKey) ?? recommendedGrouping(lines));
  const [query, setQuery] = useState("");
  const toggle = (id: string) => setExpanded((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  if (!lines.length) return <p className="px-4 py-4 text-sm text-[var(--ots-text-muted)]">No charge lines were calculated.</p>;

  const normalizedQuery = query.trim().toLowerCase();
  const visibleLines = sortTicketCostLines(normalizedQuery
    ? lines.filter((line) => [line.label, line.component?.label, line.component?.code, line.wellName, line.wellAfe]
      .some((value) => value?.toLowerCase().includes(normalizedQuery)))
    : lines);
  const groups = groupCharges(visibleLines, grouping);
  const chargeTotal = lines.reduce((total, line) => total + line.amount, 0);
  const totalMismatch = expectedTotal !== undefined && Math.abs(chargeTotal - expectedTotal) >= 0.005;
  const totalDelta = expectedTotal === undefined ? 0 : chargeTotal - expectedTotal;

  const updateGrouping = (value: TicketCostChargeGrouping) => {
    setGrouping(value);
    saveGroupingPreference(groupingPreferenceKey, value);
  };

  return (
    <div className="border-t border-[var(--ots-outline-variant)]">
      <div className="flex min-h-10 flex-wrap items-center justify-between gap-2 border-b border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] px-4 py-1.5">
        {lines.length >= searchThreshold ? (
          <label className="relative min-w-48 flex-1 sm:max-w-64">
            <span className="sr-only">Search charges</span>
            <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-[var(--ots-text-muted)]" aria-hidden="true"/>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search charges"
              className="min-h-7 w-full rounded-md border border-[var(--ots-field-outline)] bg-[var(--ots-field)] py-1 pl-7 pr-2 text-[11px] text-[var(--ots-text)] placeholder:text-[var(--ots-text-muted)] focus:border-[var(--ots-primary)] focus:outline-none"/>
          </label>
        ) : <span/>}
        <label className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-[var(--ots-text-muted)]">
          Group
          <select
            aria-label="Group charges"
            value={grouping}
            onChange={(event) => updateGrouping(event.target.value as TicketCostChargeGrouping)}
            className="min-h-7 rounded-md border border-[var(--ots-field-outline)] bg-[var(--ots-field)] px-2 py-1 text-[11px] font-medium normal-case tracking-normal text-[var(--ots-text)] focus:border-[var(--ots-primary)] focus:outline-none">
            <option value="component">Cost component</option>
            <option value="well">Well</option>
            <option value="charge">Charge name</option>
            <option value="none">No grouping</option>
          </select>
        </label>
      </div>
      <div className="grid grid-cols-[minmax(0,1fr)_4rem_5.5rem_6rem_1rem] items-center gap-x-3 border-b border-[var(--ots-outline-variant)] bg-[var(--ots-background)] px-4 py-1 text-[9px] font-semibold uppercase tracking-wide text-[var(--ots-text-muted)]">
        <span>Charge</span>
        <span className="text-right">Qty</span>
        <span className="text-right">Rate</span>
        <span className="text-right">Amount</span>
        <span aria-hidden="true"/>
      </div>
      <div className="divide-y divide-[var(--ots-outline-variant)]">
      {!visibleLines.length && <p className="px-4 py-5 text-center text-xs text-[var(--ots-text-muted)]">No charges match “{query}”.</p>}
      {groups.map((group, groupIndex) => {
        const headingId = `ticket-cost-group-${grouping}-${groupIndex}`;
        return (
        <section key={group.key} aria-labelledby={headingId}>
          <div className="grid grid-cols-[minmax(0,1fr)_4rem_5.5rem_6rem_1rem] items-center gap-x-3 bg-[var(--ots-surface-variant)] px-4 py-1.5 text-[11px] tracking-wide text-[var(--ots-text-muted)]">
            <h3 id={headingId} className="flex min-w-0 items-center gap-1.5 font-semibold">
              {grouping === "component" && group.bucket && (
                <span className="shrink-0 text-[var(--ots-text-muted)]" aria-hidden="true"><TicketCostBucketIcon bucket={group.bucket} className="size-3.5"/></span>
              )}
              <span className="truncate font-semibold text-[var(--ots-text)]">{group.label}</span>
              {group.detail && <span className="shrink-0 font-normal tracking-normal">· {group.detail}</span>}
              <span className="shrink-0 font-normal tracking-normal">({group.lines.length})</span>
            </h3>
            <span aria-hidden="true"/>
            <span aria-hidden="true"/>
            <strong className="text-right tabular-nums text-[var(--ots-text)]">
              {formatTicketCostCurrency(group.lines.reduce((total, line) => total + line.amount, 0), currency)}
            </strong>
            <span aria-hidden="true"/>
          </div>

          <div className="divide-y divide-[var(--ots-outline-variant)]">
            {group.lines.map((line) => {
              const isExpanded = expanded.has(line.id);
              const lineLabel = resolvedChargeLabel(line);
              const hasDetails = Boolean(line.component || line.resolution || line.adjustments?.length || line.wellAllocations?.length || line.costInputs?.length);
              const lineDecision = line.costInputs?.length ? getLineDecision(line.costInputs) : undefined;
              return (
                <div key={line.id}>
                  <button
                    type="button"
                    aria-expanded={isExpanded}
                    aria-label={`${isExpanded ? "Hide" : "Show"} ${lineLabel}${line.wellName ? ` for ${line.wellName}` : ""} calculation`}
                    onClick={() => toggle(line.id)}
                    className="grid w-full grid-cols-[minmax(0,1fr)_4rem_5.5rem_6rem_1rem] items-center gap-x-3 px-4 py-2.5 text-left hover:bg-[var(--ots-background)] focus-visible:outline-2 focus-visible:outline-inset focus-visible:outline-[var(--ots-primary)]">
                    <span className="min-w-0">
                      <span className="block truncate text-xs font-semibold text-[var(--ots-text)]">{lineLabel}</span>
                      <span className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] text-[var(--ots-text-muted)]">
                        <span className="truncate">
                          {formatBasis(line)}
                          {line.component && <><span aria-hidden="true"> · </span>{line.component.label}</>}
                          {line.resolution && <><span aria-hidden="true"> · </span><span className={resolutionClasses[line.resolution.state]}>{line.resolution.label}</span></>}
                        </span>
                        {line.wellName && (
                          <span className="inline-flex min-w-0 items-center gap-1 font-medium text-[var(--ots-text)]" title={`Charge for ${line.wellName}${line.wellAfe ? ` · AFE ${line.wellAfe}` : ""}`}>
                            <MapPinIcon className="size-3 shrink-0 text-[var(--ots-text-muted)]" aria-hidden="true"/>
                            <span className="truncate">{line.wellName}</span>
                            {line.wellAfe && <span className="shrink-0 font-mono text-[9px] font-normal text-[var(--ots-text-muted)]">AFE {line.wellAfe}</span>}
                          </span>
                        )}
                        {lineDecision && (
                          <span title={lineDecision.title} className={["inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold", lineDecision.classes].join(" ")}>
                            <AdjustmentsHorizontalIcon className="size-3" aria-hidden="true"/>
                            Input {lineDecision.label}
                          </span>
                        )}
                      </span>
                    </span>
                    <span className="text-right text-[11px] tabular-nums text-[var(--ots-text)]">{formatBillableQuantity(line)}</span>
                    <span className="text-right text-[11px] tabular-nums text-[var(--ots-text)]">{formatTicketCostRate(line.rate, currency, line.rateUnit)}</span>
                    <strong className="text-right text-xs tabular-nums text-[var(--ots-secondary)]">{formatTicketCostCurrency(line.amount, currency)}</strong>
                    <ChevronDownIcon className={["size-4 text-[var(--ots-text-muted)] transition-transform", isExpanded ? "rotate-180" : "", hasDetails ? "" : "invisible"].join(" ")}/>
                  </button>

                  {isExpanded && hasDetails && (
                    <div className="grid gap-4 border-t border-[var(--ots-outline-variant)] bg-[var(--ots-background)] px-4 py-3 sm:grid-cols-2 lg:grid-cols-3">
                      {(line.component || line.resolution) && (
                        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[11px]">
                          {line.component && <><dt className="text-[var(--ots-text-muted)]">Component</dt><dd className="font-medium text-[var(--ots-text)]">{line.component.label} <span className="font-mono font-normal text-[var(--ots-text-muted)]">({line.component.code})</span></dd></>}
                          {line.resolution?.systemValue && <><dt className="text-[var(--ots-text-muted)]">System</dt><dd className="font-medium text-[var(--ots-text)]">{line.resolution.systemValue}</dd></>}
                          {line.resolution?.appliedValue && <><dt className="text-[var(--ots-text-muted)]">Applied</dt><dd className="font-medium text-[var(--ots-text)]">{line.resolution.appliedValue}</dd></>}
                          {line.resolution?.reason && <><dt className="text-[var(--ots-text-muted)]">Reason</dt><dd className="text-[var(--ots-text)]">{line.resolution.reason}</dd></>}
                        </dl>
                      )}
                      {line.adjustments?.length ? (
                        <div className="space-y-1 text-[11px]">
                          {line.adjustments.map((adjustment) => (
                            <div key={adjustment.id} className="flex justify-between gap-4">
                              <span className="text-[var(--ots-text-muted)]">{adjustment.label}</span>
                              <span className="font-medium tabular-nums text-[var(--ots-text)]">{formatTicketCostCurrency(adjustment.amount, currency)}</span>
                            </div>
                          ))}
                        </div>
                      ) : null}
                      {line.wellAllocations?.length ? (
                        <div className="text-[11px]">
                          <p className="mb-1 font-semibold text-[var(--ots-text)]">Allocated wells</p>
                          <div className="space-y-1">
                            {line.wellAllocations.map((well) => (
                              <div key={well.wellId} className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-baseline gap-2">
                                <span className="truncate text-[var(--ots-text)]" title={well.wellName}>{well.wellName}</span>
                                <span className="tabular-nums text-[var(--ots-text-muted)]">{formatTicketCostPercent(well.allocation)}</span>
                                <span className="min-w-16 text-right font-medium tabular-nums text-[var(--ots-text)]">{formatTicketCostCurrency(well.amount, currency)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}
                      {line.costInputs?.length ? (
                        <section className="space-y-2 border-t border-[var(--ots-outline-variant)] pt-3 sm:col-span-2 lg:col-span-3" aria-label={`${line.label} cost input decisions`}>
                          {line.costInputs.map((input) => (
                            <div key={`${input.inputKey}-${input.rateName ?? "rate"}`}>
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <AdjustmentsHorizontalIcon className="size-3.5 text-[var(--ots-text-muted)]" aria-hidden="true"/>
                                <strong className="text-xs text-[var(--ots-text)]">{input.name}</strong>
                                <span className="font-mono text-[9px] text-[var(--ots-text-muted)]">{input.inputKey}</span>
                                <DecisionBadge decision={input.decision}/>
                                {(input.rateName || input.rateDetailDescription) && (
                                  <span className="ml-auto text-[10px] text-[var(--ots-text-muted)]">
                                    {[input.rateName, input.rateDetailDescription].filter(Boolean).join(" · ")}
                                  </span>
                                )}
                              </div>
                              <dl className="mt-2 grid gap-2 sm:grid-cols-3">
                                <CostInputValue label="System" value={input.systemValue} cost={input.systemCost} currency={currency}/>
                                <CostInputValue label="User" value={input.userValue} cost={input.candidateCost} currency={currency}/>
                                <CostInputValue label="Applied" value={input.appliedValue} cost={input.appliedCost} currency={currency}/>
                              </dl>
                              {input.reason && <p className="mt-2 text-[10px] leading-relaxed text-[var(--ots-text-muted)]"><span className="font-semibold text-[var(--ots-text)]">Reason:</span> {input.reason}</p>}
                            </div>
                          ))}
                        </section>
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
        );
      })}
      </div>
      <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-[var(--ots-outline-variant)] bg-[var(--ots-background)] px-4 py-2 text-[11px]">
        <span className="text-[var(--ots-text-muted)]">
          {normalizedQuery && `${visibleLines.length} of ${lines.length} shown`}
        </span>
        <span className="ml-auto inline-flex items-center gap-2">
          {totalMismatch && (
            <span className="inline-flex items-center gap-1 font-semibold text-[var(--ots-error)]" title={`Expected ${formatTicketCostCurrency(expectedTotal ?? 0, currency)}`}>
              <ExclamationTriangleIcon className="size-3.5" aria-hidden="true"/>
              {totalDelta > 0 ? "+" : "−"}{formatTicketCostCurrency(Math.abs(totalDelta), currency)} difference
            </span>
          )}
          <span className="text-[var(--ots-text-muted)]">{lines.length} charge{lines.length === 1 ? "" : "s"}</span>
          <strong className="tabular-nums text-[var(--ots-secondary)]">{formatTicketCostCurrency(chargeTotal, currency)}</strong>
        </span>
      </footer>
    </div>
  );
};
